<template>
  <div class="itf-root" :class="{ 'itf-root--stopping': isStopping && !updateIssueStatuDialog && !qcdialog }">

    <!-- ── AMBIENT BACKGROUND ─────────────────────────── -->
    <div class="itf-ambient" aria-hidden="true">
      <div class="itf-orb itf-orb--1"></div>
      <div class="itf-orb itf-orb--2"></div>
      <div class="itf-orb itf-orb--3"></div>
    </div>

    <!-- ── TOP BAR ────────────────────────────────────── -->
    <header class="itf-topbar">
      <v-row no-gutters align="center">

        <v-col cols="1" class="d-flex flex-column align-center">
          <div class="itf-live-indicator">
            <span class="itf-live-dot"></span>
            <span class="itf-live-lbl">LIVE SESSION</span>
          </div>
          <span class="itf-topbar-title">Focus Mode</span>
        </v-col>

        <v-col cols="11" class="d-flex justify-end align-center" style="gap:8px;">
          <span v-if="issueData.reference_num" class="itf-topbar-ref">
            {{ issueData.reference_num }}
          </span>
          <v-chip
            size="x-small" label variant="tonal"
            :class="['itf-stage-chip', `itf-stage--${issueData.stage || 'open'}`]"
          >
            {{ stageName }}
          </v-chip>
        </v-col>

      </v-row>
    </header>

    <!-- ── SKELETON → CONTENT (smooth crossfade) ────── -->
    <transition name="itf-fade" mode="out-in">

      <!-- SKELETON -->
      <div v-if="loading" key="skeleton" class="itf-body">
        <IssueTimerFocusSkeleton />
      </div>

      <!-- MAIN CONTENT -->
      <div v-else key="content" class="itf-body">
        <v-row no-gutters class="itf-main-row">

          <!-- LEFT: clock panel -->
          <v-col cols="12" md="5" class="itf-col-left">
            <TimerClockPanel
              :elapsed-seconds="elapsedSeconds"
              :estimate="issueData.estimate || null"
              :pulse="timerPulse"
              :stopping="isStopping"
              :overtime="isOvertime"
              :ref-num="issueData.reference_num || ''"
              :issue-name="issueData.name || ''"
              @stop="handleStop"
            />
          </v-col>

          <!-- RIGHT: issue detail panel -->
          <v-col cols="12" md="7" class="itf-col-right">
            <IssueDetailPanel
              :issue-data="issueData"
              :stage-name="stageName"
            />
          </v-col>
        </v-row>
      </div>

    </transition>

    <!-- ── ALL DIALOGS — teleported to body, above itf-root z-index:2000 ── -->
    <Teleport to="body">

      <!-- Update Stage -->
      <div v-if="updateIssueStatuDialog" class="itf-overlay-backdrop" @click.self="cancel()">
        <div class="itf-overlay-card" style="width:650px">
          <UpdateIssueStatus
            :stageData="filteredStages"
            @closeDialog="cancel()"
            @update-stage="updateStage"
          />
        </div>
      </div>

      <!-- Clarification -->
      <div v-if="clfnDialog" class="itf-overlay-backdrop">
        <div class="itf-overlay-card" style="width:600px">
          <CreateClarification
            @closeDialog="cancelDialog()"
            @saveDialog="saveDialog()"
            :issueId="issueData.id"
            :showDetails="showDetails"
            :followUpNo="issueData.no_of_followups"
          />
        </div>
      </div>

      <!-- Hold -->
      <div v-if="holdDialog" class="itf-overlay-backdrop">
        <div class="itf-overlay-card" style="width:600px">
          <CreateHold
            @closeDialog="cancelDialog()"
            @saveDialog="saveDialog()"
            :issueId="issueData.id"
          />
        </div>
      </div>

      <!-- Follow Up -->
      <div v-if="flpDialog" class="itf-overlay-backdrop">
        <div class="itf-overlay-card" style="width:600px">
          <CreateFollowUp
            @closeDialog="cancelDialog()"
            @saveDialog="saveDialog()"
            :issueId="issueData.id"
            :no_of_followups="issueData.no_of_followups"
          />
        </div>
      </div>

      <!-- IT Support Follow Up -->
      <div v-if="itFlpDialog" class="itf-overlay-backdrop">
        <div class="itf-overlay-card" style="width:600px">
          <CreateItFollowup
            @closeDialog="cancelDialog()"
            @saveDialog="saveDialog()"
            :issueId="issueData.id"
            :no_of_followups="issueData.no_of_followups"
          />
        </div>
      </div>

      <!-- QC Details (completed stage) -->
      <div v-if="qcDialog" class="itf-overlay-backdrop">
        <div class="itf-overlay-card" style="width:700px; min-height:550px">
          <QcDetails
            @closeDialog="cancelDialog()"
            @saveDialog="saveDialog()"
            :issueId="issueData.id"
            :CategoryId="issueData.category_id"
            :issueData="issueData"
            :subCategoryData="subCategoryData"
          />
        </div>
      </div>

      <!-- Error Log QA -->
      <div v-if="errorlogQa" class="itf-overlay-backdrop">
        <div class="itf-overlay-card" style="width:1000px">
          <ErrorLogQa
            @closeDialog="errorlogQa = false; cancelDialog();"
            @add-qa-task="errorlogQa = true"
            @updateData="updateData"
            :qaFail="true"
            :issueData="issueData"
            :timeSheetDefaults="{ issueId: issueData.id, pass: '0' }"
          />
        </div>
      </div>

      <!-- Confirmation (QA pass/close) -->
      <div v-if="confirmationDialog" class="itf-overlay-backdrop">
        <div class="itf-overlay-card" style="width:600px">
          <Confirmation
            :issueData="issueData"
            v-on:close="confirmationDialog = false; cancelDialog();"
            @saveDialog="confirmationDialog = false; saveDialog();"
            :data="issueUpdateData"
          />
        </div>
      </div>

      <!-- QC Task (qcpass / qcfail) -->
      <QcTask
        v-if="qcdialog"
        :showSection="true"
        :stage="stageData"
        :issueData="issueData"
        @close="closeQC"
      />

      <!-- ── BACK-BUTTON ALERT ──────────────────────────── -->
      <div v-if="backGuardAlert" class="itf-back-alert-backdrop">
        <div class="itf-back-alert-card" role="alertdialog" aria-modal="true" aria-labelledby="itf-bal-title">
          <div class="itf-bal-icon-ring">
            <v-icon size="30" color="#f59e0b">mdi-alert-circle-outline</v-icon>
          </div>
          <h3 id="itf-bal-title" class="itf-bal-title">Timer is still running</h3>
          <p class="itf-bal-msg">Stop the timer before leaving this screen.</p>
          <div class="itf-bal-actions">
            <button class="itf-bal-btn itf-bal-btn--stop" @click="handleBackAlertStop">
              <v-icon size="15" color="#fff" style="margin-right:5px;">mdi-stop-circle-outline</v-icon>
              Stop &amp; Go Back
            </button>
            <button class="itf-bal-btn itf-bal-btn--stay" @click="backGuardAlert = false">
              <v-icon size="15" color="#3C5AAA" style="margin-right:5px;">mdi-keyboard-return</v-icon>
              Stay
            </button>
          </div>
        </div>
      </div>

    </Teleport>

  </div>
</template>

<script>
import moment from 'moment';
import IssueService        from '@/api/service/Issue/IssueService';
import TimerService        from '@/api/service/TimeSheet/TimerService';
import StageService        from '@/api/service/Stage/StageService';
import SubCategoryService  from '@/api/service/SubCategory/SubCategoryService';
import TimerClockPanel     from './TimerClockPanel.vue';
import IssueDetailPanel    from './IssueDetailPanel.vue';
import UpdateIssueStatus   from './UpdateIssueStatus.vue';
import CreateClarification from './CreateClarification.vue';
import CreateHold          from './CreateHold.vue';
import CreateFollowUp      from './CreateFollowUp.vue';
import CreateItFollowup    from './CreateItFollowup.vue';
import QcDetails           from './QcDetails.vue';
import QcTask              from './QcTask.vue';
import Confirmation        from './Confirmation.vue';
import ErrorLogQa               from '../issues/views/QaTask.vue';
import IssueTimerFocusSkeleton  from './IssueTimerFocusSkeleton.vue';

export default {
  name: 'IssueTimerFocus',

  components: {
    TimerClockPanel, IssueDetailPanel, UpdateIssueStatus,
    CreateClarification, CreateHold, CreateFollowUp, CreateItFollowup,
    QcDetails, QcTask, Confirmation, ErrorLogQa, IssueTimerFocusSkeleton,
  },

  props: {
    issueId: { type: [Number, String], default: null },
  },

  emits: ['close', 'stopped'],

  data() {
    return {
      loading:        true,
      // timer
      issueData:      {},
      isStopping:     false,
      timerPulse:     false,
      stages:         [],
      backupStage:    [],
      elapsedSeconds: 0,
      tickInterval:   null,
      updateIssueStatuDialog: false,
      // shared with index.vue updateData flow
      userData:          {},
      mandatory:         false,
      mandatory1:        false,
      messageOfError:    '',
      bacupIssueData:    null,
      tempData:          null,
      confirmationDialog: false,
      issueUpdateData:   null,
      qcdialog:          false,
      stageData:         '',
      errorlogQa:        false,
      clfnDialog:        false,
      holdDialog:        false,
      flpDialog:         false,
      itFlpDialog:       false,
      qcDialog:          false,
      subCategoryData:   {},
      showDetails:       '',
      pendingNavigate:  false,
      backGuardAlert:   false,
    };
  },

  computed: {
    isPageMode()       { return !this.$attrs.onClose; },
    effectiveIssueId() { return this.issueId || this.$route?.query?.id; },

    isOvertime() {
      if (!this.issueData.estimate) return false;
      return this.elapsedSeconds >= this.issueData.estimate * 60;
    },

    stageName() {
      if (!this.stages.length || !this.issueData.stage) return this.issueData.stage || '';
      return this.stages.find(s => s.code === this.issueData.stage)?.name || this.issueData.stage;
    },

    filteredStages() {
      if (
        this.issueData &&
        this.issueData.parent_issue_id &&
        !this.issueData.name.startsWith("Sub task of")
      ) {
        return this.backupStage.filter(stg =>
          stg.code === "open" || stg.code === "assigned" || stg.code === "pending" ||
          stg.code === "closed" || stg.code === "cancel" ||
          stg.code === "qcpass" || stg.code === "qcfail"
        );
      } else {
        return this.backupStage.filter(stg =>
          stg.code !== "qapending" && stg.code !== "qacompleted" &&
          stg.code !== "inprogress" && stg.code !== "qcpass" && stg.code !== "qcfail"
        );
      }
    }
  },

  async created() {
    this.initTimer();
    this.userData = JSON.parse(localStorage.getItem('user_details') || '{}');
    this.stages = await StageService.findAll({ active: 1 });
    this.backupStage = [...this.stages];

    // Pre-fill from router snapshot immediately so header shows instantly
    const snapshot = window.history.state?.issueSnapshot;
    if (snapshot && snapshot.id) {
      this.issueData = snapshot;
    }

    // Always fetch full denormalized data — await so dialogs have complete issueData
    await this.getIssue();
    this.loadStages();
    window.addEventListener('keydown', this.onKey);
    // Push 2 sentinel history entries — browser back pops these first,
    // firing popstate instead of navigating away.
    window.history.pushState({ itfGuard: true }, '');
    window.history.pushState({ itfGuard: true }, '');
    window.addEventListener('popstate', this._onPopState);
  },

  beforeUnmount() {
    if (this.tickInterval) clearInterval(this.tickInterval);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('popstate', this._onPopState);
  },

  methods: {
    moment,

    // ── TIMER ────────────────────────────────────────────
    initTimer() {
      const stored = localStorage.getItem('timer');
      if (stored) {
        try {
          const td = JSON.parse(stored);
          this.elapsedSeconds = moment().diff(moment(JSON.parse(td.start)), 'seconds');
        } catch { this.elapsedSeconds = 0; }
      }
      this.tickInterval = setInterval(() => {
        this.elapsedSeconds++;
        if (this.elapsedSeconds % 60 === 0) {
          this.timerPulse = true;
          setTimeout(() => { this.timerPulse = false; }, 700);
        }
      }, 1000);
    },

    async loadStages() {
      const CACHE_KEY = 'itf_stages_cache';
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) { this.stages = JSON.parse(cached); return; }
        const data = await StageService.findAll({ active: 1 });
        this.stages = data || [];
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(this.stages));
      } catch (e) {
        console.error('IssueTimerFocus: loadStages error', e);
      }
    },

    handleBack() {
      if (this.isPageMode) this.$router.back();
      else this.$emit('close');
    },

    // ── BACK BUTTON GUARD ────────────────────────────────
    _onPopState() {
      // Re-push a sentinel immediately so rapid double-clicks are also blocked
      window.history.pushState({ itfGuard: true }, '');
      // Debounce: collapse burst clicks into one alert open
      if (this._popDebounce) return;
      this._popDebounce = true;
      setTimeout(() => { this._popDebounce = false; }, 400);
      this.backGuardAlert = true;
    },

    // "Stop & Go Back" — open the stage-update dialog (same as the Stop button)
    handleBackAlertStop() {
      this.backGuardAlert = false;
      this.handleStop();
    },

    async handleStop() {
      this.isStopping = true;
      this.updateIssueStatuDialog = true;
      if (this.tickInterval) clearInterval(this.tickInterval);
      // Disable back guard while stage dialog is active
      window.removeEventListener('popstate', this._onPopState);
    },

    cancel() {
      this.updateIssueStatuDialog = false;
      this.isStopping = false;
      // Restart the tick (re-seed from localStorage for accuracy)
      this._restartTick();
      // Re-arm the back guard
      window.history.pushState({ itfGuard: true }, '');
      window.history.pushState({ itfGuard: true }, '');
      window.addEventListener('popstate', this._onPopState);
    },

    // Restart tick interval, re-seeding elapsed time from localStorage
    _restartTick() {
      if (this.tickInterval) clearInterval(this.tickInterval);
      const stored = localStorage.getItem('timer');
      if (stored) {
        try {
          const td = JSON.parse(stored);
          this.elapsedSeconds = moment().diff(moment(JSON.parse(td.start)), 'seconds');
        } catch { /* keep current value */ }
      }
      this.tickInterval = setInterval(() => {
        this.elapsedSeconds++;
        if (this.elapsedSeconds % 60 === 0) {
          this.timerPulse = true;
          setTimeout(() => { this.timerPulse = false; }, 700);
        }
      }, 1000);
    },

    onKey(e) {
      if (e.key === 'Escape') this.handleBack();
    },

    // ── STAGE UPDATE — identical to index.vue ────────────
    async updateStage($event) {
      this.updateIssueStatuDialog = false;

      let $data = { id: this.issueData.id };
      $data['stage'] = $event;

      // Stages that open a dialog — must wait before navigating
      const dialogStages = [
        'clarification', 'hold', 'followup', 'itsupportrequired',
        'completed', 'qcpass', 'qcfail', 'errorlog', 'cancel'
      ];
      const needsDialog = dialogStages.includes($event);

      if (needsDialog) {
        // Wait for updateData so dialogs (QC, clarification, hold etc.) can open
        await this.updateData($data, 'stage');
        // If a dialog was opened by updateData, don't navigate — let the user complete it
        if (this.qcDialog || this.qcdialog || this.clfnDialog || this.holdDialog ||
            this.flpDialog || this.itFlpDialog || this.errorlogQa || this.confirmationDialog) {
          // After dialog is closed (via closeDialog / saveDialog), navigate then
          this.pendingNavigate = true;
          return;
        }
      } else {
        // Simple stages — update the stage on the backend directly,
        // then stop timer and navigate. We can't rely on updateData() because
        // $router.back() unmounts this component and kills async chains.
        await IssueService.update($data);
        await this._stopTimerDirectly();
        this.$store.dispatch('setIssueInView', this.issueData.id);
        const reloadStatus = this.$store.getters.getReloadTheIssue;
        this.$store.commit('RELOAD_THE_ISSUE', !reloadStatus);
        window.removeEventListener('popstate', this._onPopState);
        // Replace current history entry to clear the sentinel stack before navigating home
        await this.$router.replace('/');
        return;
      }

      // Timer stop + navigate after updateData completes (dialog stages)
      await this._stopTimerDirectly();
      this.$store.dispatch('setIssueInView', this.issueData.id);
      const reloadStatus2 = this.$store.getters.getReloadTheIssue;
      this.$store.commit('RELOAD_THE_ISSUE', !reloadStatus2);
      window.removeEventListener('popstate', this._onPopState);
      await this.$router.replace('/');
    },

    // Called after dialog closes — navigate if a stage update was pending
    async _navigateIfPending() {
      if (this.pendingNavigate) {
        this.pendingNavigate = false;
        await this._stopTimerDirectly();
        this.$store.dispatch('setIssueInView', this.issueData.id);
        const reloadStatus = this.$store.getters.getReloadTheIssue;
        this.$store.commit('RELOAD_THE_ISSUE', !reloadStatus);
        window.removeEventListener('popstate', this._onPopState);
        await this.$router.replace('/');
      }
    },

    /**
     * Directly stops the timer — clearing localStorage, committing store mutations,
     * and creating the timesheet entry. This is needed because in focus mode
     * the TopMenu (and its Timer.vue) are unmounted and can't respond to
     * SET_ISSUE_STATUS(2) via watchers.
     */
    async _stopTimerDirectly() {
      // 1. Stop the timesheet via API (fire-and-forget)
      try {
        await TimerService.processTime({}, 'issue', false);
      } catch (e) {
        console.error('IssueTimerFocus: _stopTimerDirectly processTime error', e);
      }

      // 2. Clear localStorage timer
      localStorage.removeItem('timer');

      // 3. Commit store mutations so other components know timer is stopped
      this.$store.commit('SET_IN_PROGRESS', false);
      this.$store.commit('SET_HIDE_TIMER', true);
      this.$store.commit('SET_TIME_STATUS', true);
      this.$store.commit('SET_ISSUE_STATUS', null);

      // 4. Refresh timesheet list
      const refreshStatus = this.$store.getters.getRefreshTimeSheet;
      this.$store.commit('SET_REFRESH_TIME_SHEET', !refreshStatus);
    },

    // Close all sub-dialogs (used internally, not wired to templates directly)
    closeDialog() {
      this.clfnDialog        = false;
      this.holdDialog        = false;
      this.qcDialog          = false;
      this.flpDialog         = false;
      this.itFlpDialog       = false;
      this.showDetails       = '';
      this.isStopping        = false;
    },

    // Called when a sub-dialog is CANCELLED — close it and return to focus timer, do NOT navigate
    cancelDialog() {
      this.clfnDialog        = false;
      this.holdDialog        = false;
      this.qcDialog          = false;
      this.flpDialog         = false;
      this.itFlpDialog       = false;
      this.errorlogQa        = false;
      this.confirmationDialog = false;
      this.showDetails       = '';
      this.pendingNavigate   = false;
      this.isStopping        = false;
      // Restore issue data to what it was before the stage change
      if (this.bacupIssueData) {
        this.issueData = JSON.parse(JSON.stringify(this.bacupIssueData));
      }
      // Restart the timer and re-arm the back guard
      this._restartTick();
      window.history.pushState({ itfGuard: true }, '');
      window.history.pushState({ itfGuard: true }, '');
      window.addEventListener('popstate', this._onPopState);
    },

    // Called when any sub-dialog is SAVED — stop timer and navigate home
    async saveDialog() {
      this.clfnDialog        = false;
      this.holdDialog        = false;
      this.qcDialog          = false;
      this.flpDialog         = false;
      this.itFlpDialog       = false;
      this.confirmationDialog = false;
      this.showDetails       = '';
      this.isStopping        = false;
      this.pendingNavigate   = false;
      if (this.tickInterval) clearInterval(this.tickInterval);
      await this._stopTimerDirectly();
      this.$store.dispatch('setIssueInView', this.issueData.id);
      const reloadStatus = this.$store.getters.getReloadTheIssue;
      this.$store.commit('RELOAD_THE_ISSUE', !reloadStatus);
      window.removeEventListener('popstate', this._onPopState);
      await this.$router.replace('/');
    },
    async closeQC(data) {
      this.qcdialog = false;
      if (data) {
        let $issueStatus = this.$store.getters.getIssueStatus;
        let $stage = data.pass == '1' ? 'qcpass' : 'qcfail';
        await IssueService.update({ stage: $stage, id: data.qa_issue_id });
        if ($issueStatus && $issueStatus == 1) {
          this.$store.commit('SET_ISSUE_STATUS', 2);
        }
        // QC process completed — stop timer and navigate to home
        this.pendingNavigate = false;
        this.isStopping = false;
        await this._stopTimerDirectly();
        this.$store.dispatch('setIssueInView', this.issueData.id);
        const reloadStatus = this.$store.getters.getReloadTheIssue;
        this.$store.commit('RELOAD_THE_ISSUE', !reloadStatus);
        window.removeEventListener('popstate', this._onPopState);
        await this.$router.replace('/');
        return;
      }
      // User cancelled — still navigate if pending
      this.isStopping = false;
      this._navigateIfPending();
    },

    showError($msg) {
      this.messageOfError = $msg;
      this.mandatory1 = true;
      setTimeout(() => { this.mandatory1 = false; }, 3000);
    },

    // ── getIssue — same as index.vue ─────────────────────
    async getIssue() {
      try {
        const id = this.issueData?.id || this.effectiveIssueId;
        if (!id) return;
        const $issueData = await IssueService.getIssueById({ issue_id: id });
        if ($issueData && $issueData.id) {
          this.issueData = $issueData;
          this.bacupIssueData = JSON.parse(JSON.stringify($issueData));
        }
      } catch (e) {
        console.error('IssueTimerFocus: getIssue error', e);
      } finally {
        this.loading = false;
      }
    },

    // ── updateData — exact copy from index.vue ───────────
    async updateData($data, $type, $value2) {
      console.log($data, '$data@@@', $type);

      if ($data.next_action) {
        await IssueService.update({ id: this.issueData.id, next_action: $data.next_action });
      }

      if ($data.stage == 'qcpass' || $data.stage == 'qcfail') {
        this.qcdialog = true;
        this.stageData = $data.stage;
      }

      if ($type == 'stage' && this.issueData.stage == 'errorlog' && $data.stage != 'errorlog' &&
          this.userData && this.userData.roles && this.userData.roles.role_type && this.userData.roles.role_type > 5) {
        this.showError('Please reach concern Team leads to change the Error log correction.');
        return true;
      }

      if (
        (await this.getAllowing($data.stage)) &&
        $data.stage !== 'clarification' &&
        $data.stage !== 'hold' &&
        $data.stage !== 'followup' &&
        $data.stage !== 'itsupportrequired' &&
        $data.stage !== 'completed'
      ) {
        if ($type == 'stage' && $data && $data.stage == 'cancel') {
          if (this.issueData.one_line_comment != null) {
            let $issueStatus = this.$store.getters.getIssueStatus;
            await IssueService.update($data);
            if ($issueStatus && $issueStatus == 1) {
              this.$store.commit('SET_ISSUE_STATUS', 2);
            }
          } else {
            this.mandatory = true;
            setTimeout(() => { this.mandatory = false; }, 3000);
          }
        } else if ($type == 'stage' && $data && $data.stage == 'errorlog' && !$data.final) {
          this.errorlogQa = true;
        } else {
          if ($data.final) {
            this.errorlogQa = false;
            delete $data.final;
          }
          if (
            this.issueData.qa_required == 1 && $type == 'stage' &&
            ($data.stage == 'closed' || $data.stage == 'qapass') &&
            (!this.issueData.parent_issue_id || (this.issueData.parent_issue_id && this.issueData.name.startsWith('Sub task of'))) &&
            await this.getCompletedQAStatus()
          ) {
            this.issueUpdateData = $data;
            this.confirmationDialog = true;
            return true;
          } else {
            if ($data.stage != 'qcpass' && $data.stage != 'qcfail') {
              let $issueStatus = this.$store.getters.getIssueStatus;
              await IssueService.update($data);
              if ($issueStatus && $issueStatus == 1) {
                this.$store.commit('SET_ISSUE_STATUS', 2);
              }
            }
          }
        }
      }

      let $keys = Object.keys($data);
      for (let $key of $keys) {
        if ($key != 'id') this.issueData[$key] = $data[$key];
      }
      if ($value2) {
        let $keys2 = Object.keys($value2);
        for (let $key of $keys2) {
          if ($key != 'id') this.issueData[$key] = $value2[$key];
        }
      }

      if ($data.stage !== 'closed' && (
        (this.tempData && this.tempData.length == 0) ||
        $data.stage == 'completed' || $data.stage == 'receivedreply' ||
        $data.stage == 'workorder' || $data.stage == 'tckpnd' ||
        $data.stage == 'cover sheet pending' || $data.stage == 'nsa' ||
        $data.stage == 'wfa' || $data.stage == 'clarification' || $data.stage == 'hold'
      )) {
        if ($type == 'stage' && $data && $data.stage == 'clarification') {
          this.clfnDialog = true;
        } else if ($type == 'stage' && $data && $data.stage == 'hold') {
          this.holdDialog = true;
        } else if ($type == 'stage' && $data && $data.stage == 'followup') {
          this.flpDialog = true;
        } else if ($type == 'stage' && $data && $data.stage == 'itsupportrequired') {
          this.itFlpDialog = true;
        } else if ($type == 'stage' && $data && $data.stage == 'completed') {
          let $subCategory = await SubCategoryService.findAll({ id: this.issueData.sub_category_id });
          if (this.issueData.qa_required == 1 || this.issueData.qc_required == 1) {
            this.subCategoryData = $subCategory[0];
            this.qcDialog = true;
          } else {
            let $issueStatus = this.$store.getters.getIssueStatus;
            await IssueService.update($data);
            if ($issueStatus && $issueStatus == 1) {
              this.$store.commit('SET_ISSUE_STATUS', 2);
            }
          }
        } else if ($type == 'stage' && $data && (
          $data.stage == 'receivedreply' || $data.stage == 'workorder' ||
          $data.stage == 'tckpnd' || $data.stage == 'cover sheet pending' ||
          $data.stage == 'nsa' || $data.stage == 'wfa'
        )) {
          let $issueStatus = this.$store.getters.getIssueStatus;
          await IssueService.update($data);
          if ($issueStatus && $issueStatus == 1) {
            this.$store.commit('SET_ISSUE_STATUS', 2);
          }
        }
      }

      await this.getIssue();
      let status2 = this.$store.getters.getReloadSubTask;
      this.$store.dispatch('issueUpdated', this.issueData.id);
      this.$store.commit('RELOAD_THE_SUB_TASK', !status2);
    },

    async getCompletedQAStatus() {
      try {
        let res = await IssueService.getCompletedQAStatus({ issueId: this.issueData.id });
        return res && res.length > 0 ? false : true;
      } catch (e) {
        console.log(e, 'getCompletedQAStatus error');
      }
    },

    async getAllowing(stage) {
      if (
        stage == 'completed' &&
        (!this.issueData.parent_issue_id || (this.issueData.parent_issue_id && this.issueData.name.startsWith('Sub task of')))
      ) {
        let res = await IssueService.getIssueClosed({ issueId: this.issueData.id });
        return res && res.length > 0 ? false : true;
      }

      if (stage == 'closed') {
        let res = await IssueService.getIssueClosed2({ issueId: this.issueData.id });
        if (res && res.message && res.message == 'Sub task not completed') {
          this.messageOfError = res.message;
          this.mandatory1 = true;
          setTimeout(() => { this.mandatory1 = false; }, 3000);
          return false;
        } else if (res && res.message && res.message == 'Please put qa task pass or fail') {
          this.messageOfError = res.message;
          this.mandatory1 = true;
          setTimeout(() => { this.mandatory1 = false; }, 3000);
          return false;
        } else if (res && res.message && res.message == 'Parent task and subtask QC should be handled by different users') {
          this.messageOfError = res.message;
          this.mandatory1 = true;
          setTimeout(() => { this.mandatory1 = false; }, 3000);
          return false;
        } else {
          return true;
        }
      }

      if (this.issueData && this.issueData.stage && this.issueData.stage == 'inprogress') {
        this.tempData = [];
        return true;
      }

      if (this.issueData && this.issueData.id) {
        let res = await IssueService.getAllowOfQa({ issueId: this.issueData.id });
        this.tempData = res;
        return res && res.length > 0 ? false : true;
      }

      return false;
    },
  },
};
</script>

<style>
/* Teleported overlay — lives directly on body, above everything */
.itf-overlay-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}
.itf-overlay-card {
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 32px rgba(0,0,0,0.24);
}

/* Vuetify autocomplete/select menus teleport to body — must sit above our backdrop */
.v-overlay.v-select__content,
.v-overlay.v-autocomplete__content,
.v-menu > .v-overlay__content,
.v-autocomplete__content,
.v-overlay--active.v-menu,
.v-overlay--active.v-select__content,
.v-overlay--active.v-autocomplete__content {
  z-index: 100002 !important;
}

/* QcTask v-dialog needs to sit above the itf-root (z-index:2000) and other overlays */
.v-overlay.v-dialog {
  z-index: 100000 !important;
}

/* CKEditor content inside teleported overlay cards */
.itf-overlay-card .ck-content p {
  font-weight: 400 !important;
}
</style>

<style scoped>
/* ── ROOT ────────────────────────────────────────────── */
.itf-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: #f8faff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Inter', system-ui, sans-serif;
  color: #0f172a;
}

.itf-root--stopping {
  pointer-events: none;
}

/* ── AMBIENT ─────────────────────────────────────────── */
.itf-ambient { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.itf-orb {
  position: absolute; border-radius: 50%;
  filter: blur(80px);
  animation: itf-drift 10s ease-in-out infinite alternate;
}
.itf-orb--1 {
  width: 480px; height: 480px;
  background: radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 70%);
  top: -140px; left: -100px; animation-duration: 10s;
}
.itf-orb--2 {
  width: 360px; height: 360px;
  background: radial-gradient(circle, rgba(6,182,212,0.08) 0%, transparent 70%);
  bottom: -80px; right: 60px; animation-duration: 13s; animation-delay: -4s;
}
.itf-orb--3 {
  width: 240px; height: 240px;
  background: radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%);
  top: 38%; left: 35%; animation-duration: 16s; animation-delay: -8s;
}
@keyframes itf-drift {
  from { transform: translate(0,0) scale(1); }
  to   { transform: translate(36px,24px) scale(1.08); }
}

/* ── TOPBAR ──────────────────────────────────────────── */
.itf-topbar {
  position: relative; z-index: 10; flex-shrink: 0;
  padding: 10px 24px;
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(226,232,240,0.9);
}
.itf-back-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 6px 13px; font-size: 13px; font-weight: 600; color: #3C5AAA;
  cursor: pointer; transition: background 0.15s, border-color 0.15s;
}
.itf-back-btn:hover { background: #eef2ff; border-color: #c7d2fe; }

.itf-live-indicator { display: flex; align-items: center; gap: 5px; }
.itf-live-dot {
  width: 6px; height: 6px; border-radius: 50%; background: #22c55e;
  animation: itf-pulse 2s ease-out infinite;
}
@keyframes itf-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.7); }
  70%  { box-shadow: 0 0 0 7px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
}
.itf-live-lbl {
  font-size: 9px; font-weight: 800; letter-spacing: 2px;
  color: #22c55e; text-transform: uppercase;
}
.itf-topbar-title { font-size: 14px; font-weight: 700; color: #1e293b; }
.itf-topbar-ref {
  font-size: 11px; font-weight: 800; color: #6366f1;
  background: #eef2ff; border: 1px solid #c7d2fe;
  padding: 2px 10px; border-radius: 99px;
}
.itf-stage-chip { font-size: 11px !important; font-weight: 700 !important; }
.itf-stage--inprogress { color: #6366f1 !important; }
.itf-stage--open       { color: #0ea5e9 !important; }
.itf-stage--pending    { color: #f59e0b !important; }
.itf-stage--closed     { color: #94a3b8 !important; }

/* ── BODY ────────────────────────────────────────────── */
.itf-body {
  flex: 1;
  overflow: hidden;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

/* ── FADE TRANSITION (skeleton → content) ────────────── */
.itf-fade-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.itf-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.itf-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.itf-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.itf-main-row {
  flex: 1;
  height: 100%;        /* fills the flex body */
}

.itf-col-left {
  height: 100%;
  overflow: hidden;
  border-right: 1px solid rgba(226,232,240,0.8);
  position: relative;
  z-index: 1;
}
.itf-col-right {
  height: 100%;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* ── RESPONSIVE ──────────────────────────────────────── */
@media (max-width: 959px) {
  .itf-body { overflow-y: auto; }
  .itf-col-left { border-right: none; border-bottom: 1px solid #e2e8f0; }
}


/* ── BACK-BUTTON ALERT ───────────────────────────────── */
.itf-back-alert-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200000;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}
.itf-back-alert-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.08);
  padding: 36px 32px 28px;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: itf-bal-in 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes itf-bal-in {
  from { opacity: 0; transform: scale(0.88) translateY(10px); }
  to   { opacity: 1; transform: scale(1)    translateY(0);    }
}
.itf-bal-icon-ring {
  width: 64px; height: 64px; border-radius: 50%;
  background: #fef3c7; border: 2px solid #fde68a;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 18px;
}
.itf-bal-title {
  font-size: 17px; font-weight: 700; color: #0f172a;
  margin: 0 0 10px; line-height: 1.3;
}
.itf-bal-msg {
  font-size: 13px; color: #64748b;
  line-height: 1.6; margin: 0 0 28px;
}
.itf-bal-actions {
  display: flex; gap: 10px; width: 100%;
}
.itf-bal-btn {
  flex: 1; padding: 11px 0; border-radius: 10px;
  font-size: 13px; font-weight: 700;
  cursor: pointer; border: none;
  display: flex; align-items: center; justify-content: center;
  transition: opacity 0.15s, transform 0.1s;
}
.itf-bal-btn:active { transform: scale(0.97); }
.itf-bal-btn--stop { background: #ef4444; color: #fff; }
.itf-bal-btn--stop:hover { opacity: 0.88; }
.itf-bal-btn--stay { background: #eef2ff; color: #3C5AAA; border: 1px solid #c7d2fe; }
.itf-bal-btn--stay:hover { background: #e0e7ff; }


</style>
